import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

interface Party {
  id: number;
  date: string;
  name: string;
  number: string;
  contact_person_name: string;
  contact_person_number: string;
  categories: string[];
  total_amount?: number;
}

const AddAmountPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const partyIdParam = searchParams.get('party');
  const navigate = useNavigate();

  const [parties, setParties] = useState<Party[]>([]);
  const [selectedPartyId, setSelectedPartyId] = useState<number | null>(
    partyIdParam ? Number(partyIdParam) : null
  );
  const [currentTotalAmount, setCurrentTotalAmount] = useState<number | null>(null);
  const [addAmount, setAddAmount] = useState<string>('');
  const [isAddingAmount, setIsAddingAmount] = useState<boolean>(false);
  const [addAmountError, setAddAmountError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Fetch party list on mount
  useEffect(() => {
    const fetchParties = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/addparty`, {
          method: 'GET',
          headers: { "Content-Type": "application/json" }
        });
        const data = await res.json();
        if (Array.isArray(data)) {
          // Fully map all fields to satisfy Party interface
          const mapped: Party[] = data.map((item: any) => ({
            id: Number(item.id),
            date: item.date || '',
            name: item.name || '',
            number: item.number || '',
            contact_person_name: item.contact_person_name || '',
            contact_person_number: item.contact_person_number || '',
            categories: Array.isArray(item.categories)
              ? item.categories 
              : typeof item.categories === 'string'
              ? item.categories.split(',').map((cat: string) => cat.trim()).filter(Boolean)
              : [],
            total_amount: item.total_amount !== undefined ? Number(item.total_amount) : undefined,
          }));
          setParties(mapped);

          // If partyId was in URL and valid, set as selected
          if (partyIdParam && !selectedPartyId) {
            const pid = Number(partyIdParam);
            if (mapped.some(p => p.id === pid)) setSelectedPartyId(pid);
          }
        } else {
          setParties([]);
        }
      } catch {
        setParties([]);
      }
    };
    fetchParties();
    // eslint-disable-next-line
  }, []);

  // Fetch current total_amount of the selected party
  useEffect(() => {
    if (!selectedPartyId) {
      setCurrentTotalAmount(null);
      return;
    }
    const current = parties.find(p => p.id === selectedPartyId);
    if (current && current.total_amount !== undefined) {
      setCurrentTotalAmount(Number(current.total_amount) || 0);
    } else {
      setCurrentTotalAmount(null);
    }
  }, [selectedPartyId, parties]);

  const handleAddAmount = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    setAddAmountError(null);
    setSuccessMsg(null);
    if (!selectedPartyId) return;
    if (!addAmount || isNaN(Number(addAmount)) || Number(addAmount) <= 0) {
      setAddAmountError("Please enter a valid amount greater than 0.");
      return;
    }

    setIsAddingAmount(true);
    try {
      // Find the complete party data by ID
      const oldParty = parties.find(p => Number(p.id) === Number(selectedPartyId));
      if (!oldParty) {
        setAddAmountError("Party not found.");
        setIsAddingAmount(false);
        return;
      }

      // Calculate new total_amount
      const baseAmount = oldParty.total_amount != null && !isNaN(Number(oldParty.total_amount))
        ? Number(oldParty.total_amount)
        : 0;
      const newTotalAmount = baseAmount + Number(addAmount);

      // Use immutable update: Only update what changed, keep rest of data as is
      // According to Party interface; send all fields exactly as they are, except updated total_amount
      // categories as a JSON string for API, like in @addparty.tsx
      const formattedCategories =
        Array.isArray(oldParty.categories)
          ? JSON.stringify(oldParty.categories)
          : typeof oldParty.categories === 'string'
          ? JSON.stringify(
              (oldParty.categories as string)
                .split(',')
                .map((cat: string) => cat.trim())
                .filter(Boolean)
            )
          : "[]";

      const dataToSend = {
        id: oldParty.id,
        date: oldParty.date ? new Date(oldParty.date).toISOString().slice(0, 10) : "",
        name: oldParty.name,
        number: oldParty.number,
        contact_person_name: oldParty.contact_person_name,
        contact_person_number: oldParty.contact_person_number,
        categories: formattedCategories,
        total_amount: newTotalAmount,
      };

      // Send PUT with all fields (API expects all, like @addparty)
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/addparty`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });

      if (res.ok) {
        setSuccessMsg("Amount added successfully!");
        setAddAmount('');
        setSelectedPartyId(null);
        setCurrentTotalAmount(null);

        // Refresh list so UI is always consistent with backend
        // (optionally could patch update UI only; here we re-fetch for safety)
        const newListRes = await fetch(`${process.env.REACT_APP_API_URL}/auth/addparty`, {
          method: 'GET',
          headers: { "Content-Type": "application/json" }
        });
        const newListData = await newListRes.json();
        if (Array.isArray(newListData)) {
          const mapped: Party[] = newListData.map((item: any) => ({
            id: Number(item.id),
            date: item.date || '',
            name: item.name || '',
            number: item.number || '',
            contact_person_name: item.contact_person_name || '',
            contact_person_number: item.contact_person_number || '',
            categories: Array.isArray(item.categories)
              ? item.categories 
              : typeof item.categories === 'string'
              ? item.categories.split(',').map((cat: string) => cat.trim()).filter(Boolean)
              : [],
            total_amount: item.total_amount !== undefined ? Number(item.total_amount) : undefined,
          }));
          setParties(mapped);
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        setAddAmountError(errorData.message || "Failed to add amount");
      }
    } catch (e: any) {
      setAddAmountError(e?.message ?? "Failed to add amount");
    }
    setIsAddingAmount(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FEF6E6] via-[#E7FEEF] to-[#E7F0FF] flex flex-col items-center justify-center py-12 px-2">
      <div className="w-full max-w-md bg-white shadow-xl rounded-3xl px-8 py-10 relative">

        <button
          className="absolute right-5 top-5 text-gray-400 hover:text-gray-900 text-xl font-bold"
          onClick={() => navigate(-1)}
          title="Back"
        >
          &larr;
        </button>

        <h1 className="text-2xl md:text-3xl font-bold text-center text-[#1CAC70] mb-7 tracking-tight" style={{ letterSpacing: "1px" }}>
          Add Amount to Party
        </h1>

        {addAmountError && (
          <div className="mb-4 rounded-lg bg-[#FFF3F1] border border-[#F9DED7] px-3 py-2 text-[#D9471D] text-center font-medium">
            {addAmountError}
          </div>
        )}
        {successMsg && (
          <div className="mb-4 rounded-lg bg-[#E3FAF6] border border-[#A6F2E9] px-3 py-2 text-[#1CAC70] text-center font-medium">
            {successMsg}
          </div>
        )}

        <form className="space-y-5" onSubmit={handleAddAmount}>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1">Select Party</label>
            <select
              className="block w-full border border-[#D6E6E7] rounded-xl py-2.5 px-4 text-base font-medium text-gray-800 bg-[#F8FAFB] outline-[#1CAC70] focus:ring-2 focus:ring-[#1CAC70] transition"
              value={selectedPartyId ?? ""}
              onChange={(e) => {
                setSelectedPartyId(Number(e.target.value));
                setAddAmount('');
                setCurrentTotalAmount(null);
                setAddAmountError(null);
                setSuccessMsg(null);
              }}
              disabled={isAddingAmount}
              style={{ boxShadow: "0 2px 8px 0 rgba(226,239,240,0.07)" }}
            >
              <option value="" disabled>Select Party</option>
              {parties.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {selectedPartyId && (
            <div>
              <div className="flex justify-between items-center">
                <label className="block text-sm font-semibold text-gray-900 mb-1">Present Total Amount</label>
                {currentTotalAmount !== null ? (
                  <span className="px-3 py-1 rounded-lg bg-[#FDF7E9] text-[#FF9500] font-semibold text-base tracking-wide border border-[#F9BB6A]">
                    ₹{currentTotalAmount}
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-lg bg-gray-200 text-gray-500 font-medium">Loading...</span>
                )}
              </div>
            </div>
          )}

          {selectedPartyId && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Amount to Add</label>
              <input
                type="number"
                min={1}
                className={`block w-full border border-[#CCD9E3] rounded-xl py-2 px-4 text-lg font-medium focus:border-[#1CAC70] focus:ring-2 focus:ring-[#1CAC70] bg-[#F5F6FA] transition ${addAmountError ? 'border-red-400' : ''}`}
                value={addAmount}
                disabled={isAddingAmount}
                onChange={e => setAddAmount(e.target.value)}
                placeholder="Enter amount"
                step="any"
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-4 pt-6">
            <button
              type="button"
              onClick={() => {
                setSelectedPartyId(null);
                setAddAmount('');
                setCurrentTotalAmount(null);
                setAddAmountError(null);
                setSuccessMsg(null);
                navigate(-1);
              }}
              disabled={isAddingAmount}
              className="px-6 py-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 font-semibold transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isAddingAmount || !selectedPartyId || !addAmount}
              className="px-8 py-2 rounded-xl text-white bg-gradient-to-r from-[#FF9500] to-[#1CAC70] font-bold shadow-md hover:from-[#FFBB33] hover:to-[#67D3A0] transition"
            >
              {isAddingAmount ? "Adding..." : "Add Amount"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAmountPage;
